import { useActiveCycleWithPhases } from "./useCycles";
import { useAppointments } from "@src/packages/care/hooks/useAppointments";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { toLocalDateString } from "@src/packages/utils/date";
import type { PgtPlan } from "../presenters/resolveConditionalCycleGraph";
import {
  resolveCycleHeaderState,
  type CycleHeaderState,
} from "./resolveCycleHeaderState";

export type { CycleHeaderState } from "./resolveCycleHeaderState";

/**
 * Adaptive cycle header state for both the Care page header and the
 * homepage Hero. Composes existing hooks; the actual decision logic
 * lives in `resolveCycleHeaderState` so it can be unit-tested without React.
 */
export function useCycleHeaderState(opts?: {
  patientId?: string | null;
  cycleId?: string | null;
  pgtPlan?: PgtPlan | null;
}): CycleHeaderState {
  const { cycle, phases } = useActiveCycleWithPhases({
    patientId: opts?.patientId,
    cycleId: opts?.cycleId,
  });

  const enableActiveQueries = !!cycle && cycle.status === "open";

  const now = new Date();
  const todayIso = toLocalDateString(now);
  const horizon = new Date(now);
  horizon.setDate(horizon.getDate() + 14);
  const horizonIso = toLocalDateString(horizon);

  const appointmentsQuery = useAppointments({
    startDate: todayIso,
    endDate: horizonIso,
    sort: "asc",
    sortBy: "startDate",
    enabled: enableActiveQueries,
  });
  const medicationsQuery = usePatientMedications({
    enabled: enableActiveQueries,
  });

  // Pure resolver. Cheap enough to run every render — skipping memoization
  // avoids needing `now` in a deps array (it changes per render by design,
  // which would defeat memoization anyway).
  return resolveCycleHeaderState({
    cycle,
    phases,
    appointments: appointmentsQuery.data ?? [],
    medications: medicationsQuery.data ?? [],
    pgtPlan: opts?.pgtPlan,
    now,
  });
}
